import { useState } from 'react';
import { CheckCircle, XCircle, ShieldOff, ShieldCheck, Settings, Users } from 'lucide-react';
import { useUsers } from '@/hooks/useUsers';
import { useAuth } from '@/hooks/useAuth';
import UserStatusBadge from '@/components/users/UserStatusBadge';
import ApproveUserDialog from '@/components/users/ApproveUserDialog';
import EditPermissionsDialog from '@/components/users/EditPermissionsDialog';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import type { CmsUser } from '@/types';
import { formatDisplayDate } from '@/utils/formatUtils';

export default function UsersPage() {
  const { cmsUser }  = useAuth();
  const {
    users, loading,
    approve, reject, deactivate, activate, updatePerms,
    isApproving, isRejecting, isDeactivating, isActivating, isUpdatingPerms,
  } = useUsers();

  const [approveTarget, setApproveTarget] = useState<CmsUser | null>(null);
  const [editTarget,    setEditTarget]    = useState<CmsUser | null>(null);

  const pending   = users.filter(u => u.status === 'pending');
  const others    = users.filter(u => u.status !== 'pending');

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-surface-900">Users</h2>
          <p className="text-sm text-surface-500 mt-0.5">{users.length} total users</p>
        </div>
        <div className="flex gap-2">
          <Badge label={`${pending.length} pending`} variant="warning" dot />
        </div>
      </div>

      {/* Pending users */}
      {pending.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-surface-700 mb-3 flex items-center gap-2">
            <Users className="h-4 w-4" /> Awaiting Approval
          </h3>
          <div className="divide-y divide-surface-100 rounded-xl border border-surface-200 bg-white overflow-hidden">
            {pending.map(user => (
              <div key={user.uid} className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-700 font-semibold text-sm">
                    {user.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-surface-900">{user.displayName}</p>
                    <p className="text-xs text-surface-400">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-surface-400 hidden sm:block">
                    {formatDisplayDate(user.createdAt)}
                  </p>
                  <Button
                    size="sm"
                    variant="danger"
                    leftIcon={<XCircle className="h-3.5 w-3.5" />}
                    loading={isRejecting}
                    onClick={() => reject(user.uid)}
                  >
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<CheckCircle className="h-3.5 w-3.5" />}
                    loading={isApproving}
                    onClick={() => setApproveTarget(user)}
                  >
                    Approve
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* All other users */}
      <section>
        <h3 className="text-sm font-semibold text-surface-700 mb-3">All Users</h3>
        <div className="rounded-xl border border-surface-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-50 border-b border-surface-200">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold text-surface-500 uppercase tracking-wide">User</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-surface-500 uppercase tracking-wide">Status</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-surface-500 uppercase tracking-wide">Role</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-surface-500 uppercase tracking-wide hidden md:table-cell">Last Active</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-surface-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-100">
              {others.map(user => (
                <tr key={user.uid} className="hover:bg-surface-50 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-semibold text-xs shrink-0">
                        {user.displayName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-surface-900 leading-none">
                          {user.displayName}
                          {user.uid === cmsUser?.uid && (
                            <span className="ml-1.5 text-xs text-surface-400">(you)</span>
                          )}
                        </p>
                        <p className="text-xs text-surface-400 mt-0.5">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <UserStatusBadge status={user.status} />
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="capitalize text-surface-700">{user.role}</span>
                  </td>
                  <td className="px-5 py-3.5 text-surface-500 hidden md:table-cell">
                    {formatDisplayDate(user.lastLoginAt)}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      {/* Edit permissions */}
                      {user.status === 'approved' && user.uid !== cmsUser?.uid && (
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={<Settings className="h-3.5 w-3.5" />}
                          onClick={() => setEditTarget(user)}
                        >
                          Permissions
                        </Button>
                      )}

                      {/* Deactivate */}
                      {user.status === 'approved' && user.uid !== cmsUser?.uid && (
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={<ShieldOff className="h-3.5 w-3.5" />}
                          loading={isDeactivating}
                          onClick={() => deactivate(user.uid)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          Deactivate
                        </Button>
                      )}

                      {/* Activate */}
                      {user.status === 'deactivated' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={<ShieldCheck className="h-3.5 w-3.5" />}
                          loading={isActivating}
                          onClick={() => activate(user.uid)}
                          className="text-emerald-600 hover:bg-emerald-50"
                        >
                          Activate
                        </Button>
                      )}

                      {/* Re-approve rejected user */}
                      {user.status === 'rejected' && (
                        <Button
                          size="sm"
                          leftIcon={<CheckCircle className="h-3.5 w-3.5" />}
                          onClick={() => setApproveTarget(user)}
                        >
                          Approve
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {others.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-sm text-surface-400">
                    No users yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Approve dialog */}
      {approveTarget && (
        <ApproveUserDialog
          user={approveTarget}
          open={!!approveTarget}
          onClose={() => setApproveTarget(null)}
          onApprove={(uid, role, permissions) => approve({ uid, role, permissions })}
          loading={isApproving}
        />
      )}

      {/* Edit permissions dialog */}
      {editTarget && (
        <EditPermissionsDialog
          user={editTarget}
          open={!!editTarget}
          onClose={() => setEditTarget(null)}
          onSave={(uid, role, permissions) => updatePerms({ uid, role, permissions })}
          loading={isUpdatingPerms}
        />
      )}
    </div>
  );
}
